import { StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, useLocation } from "react-router-dom";
import { App } from "@/App";
import { AuthProvider } from "@/auth/AuthProvider";
import "./index.css";
import { openDatePickersOnClick } from "@/lib/date-click";
import { keepWindowsOpenOnDragOut } from "@/lib/backdrop-drag";
import { SafetyScreen } from "@/components/SafetyScreen";

// A crash shows the safety screen instead of a blank page (Stephanie
// 2026-11-02); moving to another page clears it.
function SafetyByPage({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <SafetyScreen key={pathname}>{children}</SafetyScreen>;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Most CRM data is fine to refetch on focus — Stephanie + Katherine
      // both keep the tab open all day; stale data is worse than a quiet
      // background refetch.
      refetchOnWindowFocus: true,
      // 30 sec stale time — covers fast clicks without piling on requests.
      staleTime: 30_000,
      retry: (failureCount, err) => {
        // Don't retry 401s — the AuthProvider's refresh + redirect handles it
        const e = err as { status?: number };
        if (e?.status === 401) return false;
        return failureCount < 2;
      },
    },
  },
});

// The whole date box opens the calendar (Stephanie 2026-10-26).
openDatePickersOnClick(document);
// Selecting text and letting go outside a window doesn't close it (Stephanie 2026-10-27).
keepWindowsOpenOnDragOut(document);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SafetyByPage>
            <App />
          </SafetyByPage>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </StrictMode>,
);
